import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew'
import { RequestCancelAxios } from 'shared/api/lib'
import { ContactsApi } from 'entities/Contacts'
import { type IParamsContacts } from 'entities/Contacts/api/types'
import { type IParamsFiltersList } from 'entities/Broadcast/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { RowContact } from 'entities/Contacts/model/Table/RowContact'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type EnrollmentFilters } from 'widgets/ContactEnrollment'

export class EnrollmentContactsTableStore {
  private readonly _tableStore: TableStore<RowContact>
  private _itemsMap: Map<number | string, RowContact> = new Map()
  private _filters: EnrollmentFilters | null = null
  private _cancelRequest = new RequestCancelAxios()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._tableStore = new TableStore({
      key: 'enrollment_table',
      page: 1,
    })
  }

  get tableStore() {
    return this._tableStore
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get filtersListGroups(): IParamsFiltersList {
    return {
      contacts: this._filters?.contacts.map((item) => item.id) || [],
      segments: this._filters?.segments.map((item) => item.id) || [],
      tags: this._filters?.tags.map((item) => item.id) || [],
      advanced: this._filters?.filters.map((item) => item.filtersParams) || [],
    }
  }

  get expandedParams(): IParamsContacts {
    return {
      ...this._tableStore.params,
      filtersListGroups: this.filtersListGroups,
    }
  }

  onChangePagination = (page: number, length: number) => {
    this._tableStore.setPage(page)
    this._tableStore.setLimit(length)

    this.fetchContacts()
  }

  reset = () => {
    this._filters = null
    this._itemsMap.clear()
    this._tableStore.dispose()
    this._cancelRequest.reset()
  }

  fetchContacts = async () => {
    try {
      this._itemsMap.clear()
      this._cancelRequest.init()

      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      let results: Array<Contact> = []
      let total = 0

      const { data } = await ContactsApi.getContactsList(this.expandedParams, {
        cancelToken: this._cancelRequest.token,
      })

      total = data.total || 0
      results = this._entities.contactsStore.addItems(data.data || [])

      runInAction(() => {
        results.forEach((item) => {
          this._itemsMap.set(item.id, new RowContact(item))
        })

        this._tableStore.setTotal(total)
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._tableStore.setLoading(false)
      })
    }
  }

  setFilter = (filters: EnrollmentFilters) => {
    this._filters = filters
  }

  setTotal = (value: number) => {
    this._tableStore.setTotal(value)
  }
}
